import { Button } from '@mui/material';
import { AthletePreview, BlockPrintSettings } from '@strength/domain';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer, DrawerFooter, DrawerHeader } from '../../../components/drawer';
import { TabPanel, TabTrigger, Tabs } from '../../../components/tab/tab';
import { useViewer } from '../../../context/viewer';
import {
  BlockDraftWorkoutLayer,
  BlockPreview,
  BlockPublishedWorkoutLayer,
} from '../types';
import {
  DISPLAY_STOP_FORM_ID,
  DISPLAY_TAB_FORM_ID,
  DisplayTab,
} from './DisplayTab';
import { PRINT_TAB_FORM_ID, PrintTab } from './PrintTab';
import { UnsavedChangesBanner } from './UnsavedChangesBanner';

type ExportTab = 'print' | 'digitalDisplay';

interface ExportDrawerProps {
  blockWorkoutLayers: BlockDraftWorkoutLayer[];
  blockPublishedWorkoutLayers: BlockPublishedWorkoutLayer[];
  athletes: AthletePreview[];
  block: BlockPreview;
  blockPrintSettings: BlockPrintSettings;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDisplayStopped: () => void;
  initCycleId: number | undefined;
  initSessionId: number | undefined;
  initAthleteId: string | null;
  initialTab?: ExportTab;
}

export const ExportDrawer = ({
  athletes,
  block,
  open,
  onOpenChange,
  onDisplayStopped,
  initAthleteId,
  initCycleId,
  initSessionId,
  blockWorkoutLayers,
  blockPublishedWorkoutLayers,
  blockPrintSettings,
  initialTab = 'print',
}: ExportDrawerProps) => {
  const { t } = useTranslation();
  const {
    org: { hasDigitalDisplays },
  } = useViewer();
  const [activeTab, setActiveTab] = useState<ExportTab>(
    hasDigitalDisplays ? initialTab : 'print',
  );
  // If access drops while the drawer is open, fall back to print so a
  // visible panel always renders.
  const currentTab: ExportTab = hasDigitalDisplays ? activeTab : 'print';
  const [printDisabled, setPrintDisabled] = useState(true);
  const [displayDisabled, setDisplayDisabled] = useState(true);
  const [hasActiveDisplaySelection, setHasActiveDisplaySelection] =
    useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingTab, setPendingTab] = useState<ExportTab | null>(null);

  const closeHandler = () => {
    onOpenChange(false);
  };

  const handleTabChange = (newValue: ExportTab) => {
    if (newValue === activeTab) return;

    if (hasUnsavedChanges && newValue !== pendingTab) {
      setPendingTab(newValue);
      return;
    }

    setActiveTab(newValue);
    setPendingTab(null);
    setHasUnsavedChanges(false);
  };

  return (
    <Drawer open={open} onClose={closeHandler}>
      <DrawerHeader
        title={hasDigitalDisplays ? t('export') : t('print')}
        onClose={closeHandler}
      />
      {hasDigitalDisplays && (
        <div className="flex flex-col px-8 pt-2 gap-2">
          {pendingTab && (
            <UnsavedChangesBanner onDismiss={() => setPendingTab(null)} />
          )}
          <Tabs
            value={currentTab}
            onChange={(_, newValue) => handleTabChange(newValue as ExportTab)}
          >
            <TabTrigger label={t('print')} value="print" />
            <TabTrigger label={t('digitalDisplay')} value="digitalDisplay" />
          </Tabs>
        </div>
      )}

      <TabPanel value={currentTab} tab="print" className="flex flex-col grow">
        <PrintTab
          athletes={athletes}
          block={block}
          initAthleteId={initAthleteId}
          initCycleId={initCycleId}
          initSessionId={initSessionId}
          blockWorkoutLayers={blockWorkoutLayers}
          blockPrintSettings={blockPrintSettings}
          onDisabledChange={setPrintDisabled}
        />
      </TabPanel>

      {hasDigitalDisplays && (
        <TabPanel
          value={currentTab}
          tab="digitalDisplay"
          className="flex flex-col grow"
        >
          <DisplayTab
            block={block}
            blockPublishedWorkoutLayers={blockPublishedWorkoutLayers}
            initCycleId={initCycleId}
            initSessionId={initSessionId}
            onOpenChange={onOpenChange}
            onDisplayStopped={onDisplayStopped}
            onDisabledChange={setDisplayDisabled}
            onUnsavedChangesChange={setHasUnsavedChanges}
            onActiveSelectionChange={setHasActiveDisplaySelection}
          />
        </TabPanel>
      )}

      {currentTab === 'print' ? (
        <DrawerFooter className="justify-end flex flex-row gap-2 w-full p-8">
          <Button onClick={closeHandler}>{t('cancel')}</Button>
          <Button
            type="submit"
            form={PRINT_TAB_FORM_ID}
            disabled={printDisabled}
          >
            {t('print')}
          </Button>
        </DrawerFooter>
      ) : (
        <DrawerFooter className="flex flex-row justify-between w-full p-8">
          {hasActiveDisplaySelection ? (
            <Button
              type="submit"
              form={DISPLAY_STOP_FORM_ID}
              variant="containedDestructive"
            >
              {t('stopDisplaying')}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex flex-row gap-2">
            <Button onClick={closeHandler}>{t('cancel')}</Button>
            <Button
              type="submit"
              form={DISPLAY_TAB_FORM_ID}
              disabled={displayDisabled}
            >
              {t('save')}
            </Button>
          </div>
        </DrawerFooter>
      )}
    </Drawer>
  );
};
